import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import BaseDialog from '../BaseDialog/BaseDialog';
import styles from './FormDialog.module.css';

// Context to expose state and controls to nested children
const FormDialogContext = createContext(null);

/**
 * Custom hook to consume the FormDialog context.
 * Allows any nested child component to dynamically manage errors, loading states, and submit button locks.
 * 
 * @returns {{
 *   error: string|React.ReactNode,
 *   setError: (err: string|React.ReactNode) => void,
 *   clearError: () => void,
 *   confirmDisabled: boolean,
 *   setConfirmDisabled: (disabled: boolean) => void,
 *   loading: boolean,
 *   setLoading: (loading: boolean) => void
 * }}
 * 
 * @example
 * ```jsx
 * // 1. Using the context hook inside a nested child component:
 * const FormFields = () => {
 *   const { setError, clearError, setConfirmDisabled } = useFormDialog();
 *   const [name, setName] = useState('');
 * 
 *   useEffect(() => {
 *     if (!name) {
 *       setConfirmDisabled(true);
 *       setError('Name is required.');
 *     } else {
 *       setConfirmDisabled(false);
 *       clearError();
 *     }
 *   }, [name]);
 * 
 *   return <input value={name} onChange={e => setName(e.target.value)} />;
 * };
 * ```
 */
export const useFormDialog = () => {
  const context = useContext(FormDialogContext);
  return context || {
    error: '',
    setError: () => {},
    clearError: () => {},
    confirmDisabled: false,
    setConfirmDisabled: () => {},
    loading: false,
    setLoading: () => {}
  };
};

/**
 * @typedef {Object} FormDialogCustomProps
 * @property {(e: import('react').FormEvent<HTMLFormElement>) => void} [onSubmit] - Submit handler for the form.
 * @property {() => void} [onClick] - Alternate handler for form submission/click.
 * @property {string | import('react').ReactNode} [error] - Error message string or element to display in the footer.
 * @property {string | import('react').ReactNode} [errorMessage] - Alternative alias for error message.
 * @property {string | import('react').ReactNode} [actionMessage] - Success or status message to display in the footer.
 * @property {boolean} [submitDisabled] - Selectively disables ONLY the submit/confirm button.
 * @property {boolean} [isSubmitDisabled] - Alternative alias for submitDisabled.
 * @property {boolean} [saveButtonDisabled] - Legacy alias for submitDisabled.
 * @property {string} [saveButtonText] - Legacy alias for confirmLabel.
 * @property {string} [cancelButtonText] - Legacy alias for cancelLabel.
 * @property {boolean} [hideCancelButton] - Legacy alias for hideCancel.
 * @property {boolean} [hideSaveButton] - Legacy alias for hideConfirm.
 * 
 * @typedef {Omit<import('../BaseDialog/BaseDialog').BaseDialogProps, 'onConfirm'> & FormDialogCustomProps} FormDialogProps
 */

/**
 * FormDialog component designed to wrap form fields with high visual aesthetics.
 * Offers built-in status displays, proper HTML form submission, and exposes state/controls
 * to child components via both Context and Render Props.
 * 
 * @param {FormDialogProps} props
 * @returns {React.ReactElement}
 * 
 * @example
 * ```jsx
 * // 2. Using the Render Props (Function-as-a-Child) pattern:
 * <FormDialog title="Register Form" onSubmit={handleSave}>
 *   {({ setError, setConfirmDisabled, clearError }) => (
 *     <input 
 *       placeholder="Email" 
 *       onChange={e => {
 *         if (!e.target.value.includes('@')) {
 *           setError('Invalid email');
 *           setConfirmDisabled(true);
 *         } else {
 *           clearError();
 *           setConfirmDisabled(false);
 *         }
 *       }} 
 *     />
 *   )}
 * </FormDialog>
 * ```
 */
const FormDialog = ({
  onSubmit,
  onClick,
  error,
  errorMessage,
  actionMessage,
  submitDisabled = false,
  isSubmitDisabled = false,
  saveButtonDisabled = false,
  confirmLabel,
  saveButtonText,
  cancelLabel,
  cancelButtonText,
  hideCancel,
  hideCancelButton = false,
  hideConfirm,
  hideSaveButton = false,
  loading = false,
  children,
  className = '',
  ...rest
}) => {
  // 1. Setup local states for dynamic control by children
  const [internalError, setInternalError] = useState('');
  const [internalConfirmDisabled, setInternalConfirmDisabled] = useState(false);
  const [internalLoading, setInternalLoading] = useState(false);

  // 2. Synchronize parent props into local states when props change
  const propError = error !== undefined ? error : errorMessage;
  useEffect(() => {
    setInternalError(propError || '');
  }, [propError]);

  const propSubmitLocked = submitDisabled || isSubmitDisabled || saveButtonDisabled;
  useEffect(() => {
    setInternalConfirmDisabled(!!propSubmitLocked);
  }, [propSubmitLocked]);

  useEffect(() => {
    setInternalLoading(!!loading);
  }, [loading]);

  // 3. Resolve display variables based on current local states
  const displayConfirmLabel = confirmLabel !== undefined ? confirmLabel : (saveButtonText !== undefined ? saveButtonText : 'Save');
  const displayCancelLabel = cancelLabel !== undefined ? cancelLabel : (cancelButtonText !== undefined ? cancelButtonText : 'Cancel');
  
  const shouldHideCancel = hideCancel !== undefined ? hideCancel : hideCancelButton;
  const shouldHideConfirm = hideConfirm !== undefined ? hideConfirm : hideSaveButton;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit(e);
    } else if (onClick) {
      onClick(e);
    }
  };

  // 4. Create the context value object
  const contextValue = useMemo(() => ({
    error: internalError,
    setError: setInternalError,
    clearError: () => setInternalError(''),
    confirmDisabled: internalConfirmDisabled,
    setConfirmDisabled: setInternalConfirmDisabled,
    loading: internalLoading,
    setLoading: setInternalLoading
  }), [internalError, internalConfirmDisabled, internalLoading]);

  // Render both error and actionMessage beautifully on the left side of the footer
  const footerLeftContent = (internalError || actionMessage) ? (
    <div className={styles.statusContainer}>
      {internalError && (
        <span className={`${styles.errorLabel} form-dialog-error-label`}>
          {internalError}
        </span>
      )}
      {actionMessage && (
        <span className={`${styles.actionMessage} form-dialog-action-message`}>
          {actionMessage}
        </span>
      )}
    </div>
  ) : null;

  return (
    <FormDialogContext.Provider value={contextValue}>
      <BaseDialog
        PaperComponent="form"
        paperProps={{ onSubmit: handleSubmit }}
        type="submit" // Triggers HTML5 form submit behavior on the primary button
        confirmLabel={displayConfirmLabel}
        cancelLabel={displayCancelLabel}
        hideCancel={shouldHideCancel}
        hideConfirm={shouldHideConfirm}
        confirmDisabled={internalConfirmDisabled}
        loading={internalLoading}
        footerLeftContent={footerLeftContent}
        className={`${className} form-dialog-root`}
        {...rest}
      >
        {/* Support both Function-as-a-Child (render props) and standard children rendering */}
        {typeof children === 'function' ? children(contextValue) : children}
      </BaseDialog>
    </FormDialogContext.Provider>
  );
};

export default FormDialog;
